/**
 * PDP media gallery: track, dots, thumbnails, prev/next.
 *
 * Was `snippets/product-gallery.liquid`'s {% javascript %} tag. Shopify concatenates
 * those into one `snippet-scripts.js` that every page downloads and parses, guarded
 * at runtime — 18 KB of PDP-only code shipping to the home page and the cart. As an
 * asset it loads from `blocks/product-gallery.liquid`, so only a page with a gallery
 * pays for it.
 *
 * The window guard is what the shared tag gave us for free: the snippet can render
 * more than once (a PDP with two gallery blocks), and two identical <script src> tags
 * both execute — the per-root guard below stops a double init, but not the document
 * listeners at the bottom.
 */
(function () {
  if (window.__pdpGalleryInit) return;
  window.__pdpGalleryInit = true;

  function initGallery(root) {
    if (root.__pdpGalleryInit) return;
    root.__pdpGalleryInit = true;

    var track = root.querySelector('[data-pdp-gallery-track]');
    var slides = root.querySelectorAll('[data-pdp-gallery-slide]');
    var dots = root.querySelectorAll('[data-pdp-gallery-dot]');
    var thumbs = root.querySelectorAll('[data-pdp-gallery-thumb]');
    var prev = root.querySelector('[data-pdp-gallery-prev]');
    var next = root.querySelector('[data-pdp-gallery-next]');
    var count = slides.length;
    if (!track || count === 0) return;
    var loop = root.getAttribute('data-loop') !== 'false';
    var fade = root.getAttribute('data-transition') === 'fade';
    var index = 0;

    // Fade images in as they load. Runs synchronously so cached images (which
    // are already `complete`) get `is-loaded` before the next paint — no flash.
    root.classList.add('is-fade-ready');
    root.querySelectorAll('.pdp-gallery__img').forEach(function (img) {
      if (img.tagName !== 'IMG') return;
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('is-loaded');
        return;
      }
      var mark = function () { img.classList.add('is-loaded'); };
      img.addEventListener('load', mark, { once: true });
      img.addEventListener('error', mark, { once: true });
    });

    /* ---- Inline video playback ----------------------------------------
       Videos autoplay, but never all at once: only the active slide plays,
       and only while the gallery is on screen. The `autoplay` attribute is
       deliberately absent from the markup — it would start every video in the
       strip the moment its metadata arrived, decoding several files in
       parallel for media the shopper hasn't reached yet.
       `muted` is re-asserted in JS because autoplay is refused for anything
       audible, and iOS in particular drops the attribute when markup is
       injected rather than parsed (the PDP carousels do exactly that). */
    var onScreen = true;

    function playVideo(v) {
      v.muted = true;
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }

    function syncVideos() {
      slides.forEach(function (s, i) {
        var v = s.querySelector('[data-pdp-gallery-video]');
        if (!v) return;
        if (i === index && onScreen) {
          playVideo(v);
        } else {
          // Unconditional, NOT guarded by `if (!v.paused)`: a video whose
          // play() promise is still pending reports `paused === true`, so that
          // guard skipped it and the deferred playback then started on a slide
          // the shopper had already navigated away from. pause() aborts the
          // pending play instead.
          v.pause();
          // Rewind so a slide the shopper comes back to opens on its first
          // frame instead of resuming mid-shot.
          if (v.currentTime) v.currentTime = 0;
        }
      });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { onScreen = e.isIntersecting; });
        syncVideos();
      }, { threshold: 0.1 }).observe(root);
    }

    function render() {
      if (fade) {
        slides.forEach(function (s, i) { s.classList.toggle('is-active', i === index); });
      } else {
        track.style.transform = 'translateX(' + (-index * 100) + '%)';
      }
      dots.forEach(function (d, i) { d.classList.toggle('is-active', i === index); });
      thumbs.forEach(function (t, i) { t.classList.toggle('is-active', i === index); });
      if (!loop) {
        if (prev) prev.classList.toggle('is-disabled', index === 0);
        if (next) next.classList.toggle('is-disabled', index === count - 1);
      }
      syncVideos();
    }

    function go(i) {
      if (loop) {
        index = (i + count) % count;
      } else {
        index = Math.max(0, Math.min(count - 1, i));
      }
      render();
    }

    if (prev) prev.addEventListener('click', function () { go(index - 1); });
    if (next) next.addEventListener('click', function () { go(index + 1); });
    dots.forEach(function (d, i) { d.addEventListener('click', function () { go(i); }); });
    thumbs.forEach(function (t, i) { t.addEventListener('click', function () { go(i); }); });

    /* ---- Variant → slide ----------------------------------------------
       When the chosen variant has an image of its own (gift card designs),
       bring it forward. Silent no-op for variants without media, so the
       size-only products that make up the rest of the catalogue are
       untouched: the map is empty and there's nothing to look up. */
    var variantMedia = {};
    try {
      variantMedia = JSON.parse(root.getAttribute('data-variant-media') || '{}');
    } catch (e) { /* noop */ }

    function slideIndexForMedia(mediaId) {
      for (var i = 0; i < count; i++) {
        if (slides[i].getAttribute('data-media-id') === String(mediaId)) return i;
      }
      return -1;
    }

    function goToVariant(id) {
      if (!id) return;
      var mediaId = variantMedia[String(id)];
      if (!mediaId) return;
      var target = slideIndexForMedia(mediaId);
      // The featured media can be excluded from the gallery, so a variant may
      // point at a slide that isn't rendered here.
      if (target < 0 || target === index) return;
      go(target);
    }

    document.addEventListener('variant:change', function (e) {
      goToVariant(e.detail && e.detail.id);
    });

    // A ?variant=… link opens on that variant's image. Read here rather than
    // waiting for a `variant:change` from the picker: both run at load and
    // their order inside the bundled theme JS isn't guaranteed, so an event
    // fired during the picker's init could land before this listener exists.
    try {
      goToVariant(new URLSearchParams(window.location.search).get('variant'));
    } catch (e) { /* noop */ }

    var zoomDesktop = root.getAttribute('data-zoom-desktop') === 'true';
    var zoomMobile = root.getAttribute('data-zoom-mobile') === 'true';
    var doubleTapToZoom = root.getAttribute('data-mobile-zoom-trigger') === 'double_tap';

    // Only slides that carry a still to enlarge can be zoomed. Videos are
    // rendered without `data-zoom-src` (see the slide markup), so they never
    // open the lightbox.
    function zoomableAt(i) {
      return !!(slides[i] && slides[i].getAttribute('data-zoom-src'));
    }

    // Zoom availability is per breakpoint, evaluated at gesture time so it
    // follows viewport resizes (and the theme editor's device preview).
    function zoomAllowed() {
      if (!zoomableAt(index)) return false;
      if (window.matchMedia('(min-width: 1024px)').matches) return zoomDesktop;
      return zoomMobile;
    }

    /* ---- Touch gestures on the hero (mobile) --------------------------
       The gallery sits in the middle of a vertically scrolling page, so a
       touch that starts here is far more often a scroll than an intent to
       zoom. Only a deliberate tap opens the lightbox: the finger must stay
       within TAP_SLOP px in BOTH axes and lift within TAP_MAX_MS. Anything
       that drifts or is held is a scroll or a press and never zooms.
       A slide change additionally requires the gesture to be clearly
       horizontal, so a diagonal scroll no longer flips the image.
       All four thresholds are merchant-tunable (data attributes above), with
       the defaults below as fallback when the settings are absent. */
    var SWIPE_MIN = parseInt(root.getAttribute('data-swipe-min'), 10) || 40;
    var TAP_SLOP = parseInt(root.getAttribute('data-tap-slop'), 10) || 10;
    var TAP_MAX_MS = parseInt(root.getAttribute('data-tap-max-ms'), 10) || 250;
    var DOUBLE_TAP_MS = parseInt(root.getAttribute('data-double-tap-ms'), 10) || 300;
    var startX = null;
    var startY = 0;
    var startT = 0;
    var touchMoved = false;
    var multiTouch = false;
    var lastTapT = 0;
    var lastTouchEndT = -1e9;

    track.addEventListener('touchstart', function (e) {
      if (e.touches.length > 1) {
        multiTouch = true;
        return;
      }
      multiTouch = false;
      touchMoved = false;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startT = e.timeStamp;
    }, { passive: true });

    track.addEventListener('touchmove', function (e) {
      if (startX === null) return;
      var dx = e.touches[0].clientX - startX;
      var dy = e.touches[0].clientY - startY;
      if (Math.abs(dx) > TAP_SLOP || Math.abs(dy) > TAP_SLOP) touchMoved = true;
    }, { passive: true });

    track.addEventListener('touchend', function (e) {
      lastTouchEndT = e.timeStamp;
      if (startX === null || multiTouch) {
        startX = null;
        return;
      }
      var touch = e.changedTouches[0];
      var dx = touch.clientX - startX;
      var dy = touch.clientY - startY;
      var dt = e.timeStamp - startT;
      startX = null;

      if (Math.abs(dx) > SWIPE_MIN && Math.abs(dx) > Math.abs(dy)) {
        go(dx < 0 ? index + 1 : index - 1);
        lastTapT = 0;
        return;
      }

      // Scroll or long press — not a tap.
      if (touchMoved || dt > TAP_MAX_MS) {
        lastTapT = 0;
        return;
      }

      if (!zoomAllowed()) return;

      if (doubleTapToZoom) {
        if (e.timeStamp - lastTapT < DOUBLE_TAP_MS) {
          lastTapT = 0;
          openZoom();
        } else {
          lastTapT = e.timeStamp;
        }
        return;
      }

      openZoom();
    });

    // Mouse: click the main image to open the zoom. Touch devices also emit a
    // synthetic click right after touchend, so ignore clicks that closely
    // follow a touch — the gesture logic above already decided.
    track.addEventListener('click', function (e) {
      if (e.timeStamp - lastTouchEndT < 700) return;
      if (!zoomAllowed()) return;
      openZoom();
    });

    /* ---- Full-screen zoom lightbox ------------------------------------ */
    var zoom = root.querySelector('[data-pdp-zoom]');
    var zoomImg = zoom && zoom.querySelector('[data-pdp-zoom-img]');
    var zoomViewport = zoom && zoom.querySelector('[data-pdp-zoom-viewport]');
    var zoomClose = zoom && zoom.querySelector('[data-pdp-zoom-close]');
    var zoomPrev = zoom && zoom.querySelector('[data-pdp-zoom-prev]');
    var zoomNext = zoom && zoom.querySelector('[data-pdp-zoom-next]');
    var zoomCounter = zoom && zoom.querySelector('[data-pdp-zoom-counter]');
    var zoomMode = (zoom && zoom.getAttribute('data-zoom-mode')) || 'contain';
    var zoomWidthPct = parseFloat(zoom && zoom.getAttribute('data-zoom-width')) || 80;
    var zoomOpen = false;
    var scale = 1;
    var tx = 0;
    var ty = 0;
    var pointers = new Map();
    var pinchStartDist = 0;
    var pinchStartScale = 1;
    var lastPanX = 0;
    var lastPanY = 0;
    var lastTapTime = 0;
    var MAX_SCALE = 4;

    function applyZoomTransform() {
      // Clamp the pan so the image can't be dragged past its own edges.
      var vpW = zoomViewport.clientWidth;
      var vpH = zoomViewport.clientHeight;
      var iw = zoomImg.clientWidth * scale;
      var ih = zoomImg.clientHeight * scale;
      var maxX = Math.max(0, (iw - vpW) / 2);
      var maxY = Math.max(0, (ih - vpH) / 2);
      tx = Math.max(-maxX, Math.min(maxX, tx));
      ty = Math.max(-maxY, Math.min(maxY, ty));
      zoomImg.style.setProperty('--zoom-scale', scale);
      zoomImg.style.setProperty('--zoom-x', tx + 'px');
      zoomImg.style.setProperty('--zoom-y', ty + 'px');
      zoom.classList.toggle('is-zoomed', scale > 1.01);
    }

    function resetZoomTransform() {
      scale = 1;
      tx = 0;
      ty = 0;
      applyZoomTransform();
    }

    // Pick the CDN width to request based on how many device pixels the
    // image will actually occupy: (displayed CSS size) x devicePixelRatio.
    // - contain: fitted by height on landscape -> displayed width = vh * ar.
    // - width: the image fills a fixed % of the viewport width.
    // Snapped to steps so the CDN cache is reused; capped at Shopify's max.
    function bestZoomWidth(ar) {
      var dpr = window.devicePixelRatio || 1;
      var displayW;
      if (zoomMode === 'width') {
        displayW = window.innerWidth * (zoomWidthPct / 100);
      } else {
        displayW = Math.min(window.innerWidth, window.innerHeight * ar);
      }
      var target = Math.ceil(displayW * dpr);
      var steps = [1024, 1440, 1600, 2048, 2560, 3200, 4096, 5000, 5760];
      for (var i = 0; i < steps.length; i++) {
        if (steps[i] >= target) return steps[i];
      }
      return 5760;
    }

    function syncZoomImage() {
      var slide = slides[index];
      var base = slide && slide.getAttribute('data-zoom-src');
      var ar = parseFloat(slide && slide.getAttribute('data-zoom-ar')) || (2 / 3);
      var src = '';
      if (base) src = base.replace(/width=\d+/, 'width=' + bestZoomWidth(ar));
      if (src && zoomImg.getAttribute('src') !== src) zoomImg.setAttribute('src', src);
      var img = slide && slide.querySelector('.pdp-gallery__img');
      zoomImg.alt = (img && img.alt) || '';
      if (zoomCounter) {
        zoomCounter.textContent = (index + 1) + ' / ' + count;
      }
      if (zoomViewport) zoomViewport.scrollTop = 0;
      resetZoomTransform();
    }

    // Navigation inside the lightbox steps over video slides — there is no
    // still for them, so landing on one would leave the previous image on
    // screen with a stale counter.
    function goZoom(dir) {
      for (var step = 1; step <= count; step++) {
        var i = index + dir * step;
        if (!loop && (i < 0 || i > count - 1)) return;
        var j = ((i % count) + count) % count;
        if (zoomableAt(j)) {
          go(j);
          syncZoomImage();
          return;
        }
      }
    }

    function onZoomKeydown(e) {
      if (e.key === 'Escape') closeZoom();
      else if (e.key === 'ArrowRight') goZoom(1);
      else if (e.key === 'ArrowLeft') goZoom(-1);
    }

    function openZoom() {
      if (!zoom || zoomOpen) return;
      zoomOpen = true;
      syncZoomImage();
      zoom.hidden = false;
      // Lock the page behind the overlay. The class drives `overflow: hidden`
      // on the ROOT element (assets/critical.css) — setting it on <body>
      // instead does nothing, since body's overflow only propagates to the
      // viewport while html's is `visible`.
      document.documentElement.classList.add('is-scroll-locked');
      document.addEventListener('keydown', onZoomKeydown);
      // Focus the overlay itself, not the close button: Safari paints its
      // default focus ring on programmatically focused controls, which read as
      // a stray box around the ×. The overlay is a non-interactive container
      // (tabindex="-1", ring hidden in CSS) and keyboard handling lives on
      // document, so Escape/arrows keep working from here.
      zoom.focus();
    }

    function closeZoom() {
      if (!zoomOpen) return;
      zoomOpen = false;
      zoom.hidden = true;
      document.documentElement.classList.remove('is-scroll-locked');
      document.removeEventListener('keydown', onZoomKeydown);
      resetZoomTransform();
    }

    if (zoom) {
      if (zoomClose) zoomClose.addEventListener('click', closeZoom);
      if (zoomPrev) zoomPrev.addEventListener('click', function () { goZoom(-1); });
      if (zoomNext) zoomNext.addEventListener('click', function () { goZoom(1); });

      // Click on the empty backdrop (not the image) closes. Works in both
      // modes and never fires while scrolling or interacting with the image.
      zoomViewport.addEventListener('click', function (e) {
        if (e.target === zoomViewport) closeZoom();
      });

      function dist(a, b) {
        var dx = a.x - b.x;
        var dy = a.y - b.y;
        return Math.sqrt(dx * dx + dy * dy);
      }

      // Custom pinch/pan gestures apply only to "contain" mode. In "width"
      // mode the browser's native vertical scroll (and native pinch on
      // touch) handles magnification, so we leave the viewport alone.
      if (zoomMode === 'contain') {
      // Touch gestures: pinch-to-zoom, pan when zoomed, double-tap to toggle.
      zoomViewport.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse') return;
        e.preventDefault();
        zoomViewport.setPointerCapture(e.pointerId);
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.size === 2) {
          var pts = Array.from(pointers.values());
          pinchStartDist = dist(pts[0], pts[1]);
          pinchStartScale = scale;
        } else if (pointers.size === 1) {
          lastPanX = e.clientX;
          lastPanY = e.clientY;
          var now = e.timeStamp;
          if (now - lastTapTime < 300) {
            // Double-tap: toggle between fit and 2.5x.
            scale = scale > 1.01 ? 1 : 2.5;
            tx = 0;
            ty = 0;
            applyZoomTransform();
            lastTapTime = 0;
          } else {
            lastTapTime = now;
          }
        }
      });

      zoomViewport.addEventListener('pointermove', function (e) {
        if (e.pointerType === 'mouse') return;
        if (!pointers.has(e.pointerId)) return;
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.size === 2 && pinchStartDist > 0) {
          var pts = Array.from(pointers.values());
          var ratio = dist(pts[0], pts[1]) / pinchStartDist;
          scale = Math.max(1, Math.min(MAX_SCALE, pinchStartScale * ratio));
          zoom.classList.add('is-dragging');
          applyZoomTransform();
        } else if (pointers.size === 1 && scale > 1.01) {
          tx += e.clientX - lastPanX;
          ty += e.clientY - lastPanY;
          lastPanX = e.clientX;
          lastPanY = e.clientY;
          zoom.classList.add('is-dragging', 'is-panning');
          applyZoomTransform();
        }
      });

      function endPointer(e) {
        if (e.pointerType === 'mouse') return;
        pointers.delete(e.pointerId);
        if (pointers.size < 2) pinchStartDist = 0;
        if (pointers.size === 0) {
          zoom.classList.remove('is-dragging', 'is-panning');
          if (scale <= 1.01) resetZoomTransform();
        }
      }
      zoomViewport.addEventListener('pointerup', endPointer);
      zoomViewport.addEventListener('pointercancel', endPointer);
      } // end contain-only gestures
    }

    render();
  }

  function initAll() {
    document.querySelectorAll('[data-pdp-gallery]').forEach(initGallery);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
